import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useTaskStore } from '../../stores/taskStore';
import { useUIStore } from '../../stores/uiStore';
import { Input } from '../common';
import { NotificationsDropdown } from '../common/NotificationsDropdown';
import { useBellUnreadCount } from '../../contexts/NotificationStateContext';
import { OrganizationSelector } from '../organizations/OrganizationSelector';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ title, showSearch = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const updateSearchAndFetch = useTaskStore((state) => state.updateSearchAndFetch);
  const { mobileMenuOpen, toggleMobileMenu, darkMode, toggleDarkMode } = useUIStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const { unreadCount } = useBellUnreadCount();

  // FIX: Debounced search for real-time filtering on keystroke
  // Triggers search 300ms after user stops typing
  // Uses updateSearchAndFetch to preserve existing filters (like view)
  // Use ref to track the pending timer for proper cleanup
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Apply search with debounce - handles both search and clear
  const handleSearchChange = (value: string) => {
    // Update input immediately for responsiveness
    setSearchQuery(value);
    
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Debounce the actual search
    debounceTimerRef.current = setTimeout(() => {
      const trimmedValue = value.trim();
      
      if (trimmedValue === '') {
        // User cleared the search - restore original list
        updateSearchAndFetch(undefined);
      } else {
        // User is searching
        updateSearchAndFetch(trimmedValue);
      }
    }, 300);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Focus input when mobile search opens
  useEffect(() => {
    if (mobileSearchOpen && mobileSearchRef.current) {
      mobileSearchRef.current.focus();
    }
  }, [mobileSearchOpen]);

  // Close mobile search on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileSearchOpen) {
        setMobileSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileSearchOpen]);

  // Handle closing mobile search overlay - clears input and search filter
  const handleCloseSearch = () => {
    setMobileSearchOpen(false);
    setSearchQuery('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    updateSearchAndFetch(undefined);
  };

  return (
    <header className="relative h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 md:px-6 pt-[env(safe-area-inset-top)]">
      {/* ====== Mobile search overlay (absolute, z-20 above header content) ====== */}
      {showSearch && mobileSearchOpen && (
        <div className="absolute inset-0 flex items-center gap-2 px-4 bg-white dark:bg-gray-900 z-20 sm:hidden">
          <button
            onClick={handleCloseSearch}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
            aria-label={t('common.closeSearch')}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <Input
              ref={mobileSearchRef}
              type="search"
              placeholder={t('common.searchTasks')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="bg-gray-50 dark:bg-gray-800 border-0"
            />
          </div>
        </div>
      )}
      {/* ====== Left side: Hamburger + Title ====== */}
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        {/* Mobile menu button - only visible on mobile */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
          aria-label={mobileMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Title - always visible, truncated if needed */}
        <h1 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate min-w-0">
          {title}
        </h1>
      </div>

      {/* gap-1 (4px) on xs, gap-2 (8px) on sm+, gap-4 (16px) on md+ */}
      <div className="flex items-center gap-1 sm:gap-2 md:gap-4 flex-shrink-0">
        {/* Organization Selector */}
        <OrganizationSelector />

        {/* Desktop Search - only shown when showSearch is true - real-time search with 300ms debounce */}
        {showSearch && (
          <div className="hidden sm:block w-48 md:w-64 flex-shrink-0">
            <Input
              type="search"
              placeholder={t('common.searchTasks')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="bg-gray-50 dark:bg-gray-800 border-0"
            />
          </div>
        )}

        {/* Mobile search button */}
        {showSearch && (
          <button
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            className="sm:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
            aria-label={t('common.searchTasks')}
          >
            <Search className="w-5 h-5" />
          </button>
        )}

        {/* Dark mode toggle - only on sm screens (640-767px).
            xs (<640px): use mobile sidebar toggle.
            md+ (768px+): desktop sidebar has its own toggle. */}
        <button
          onClick={toggleDarkMode}
          className="hidden sm:flex md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] items-center justify-center flex-shrink-0"
          aria-label={darkMode ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
        >
          {darkMode ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* Notifications */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            onMouseDown={(e) => e.stopPropagation()}
            className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center" aria-live="polite" aria-label={t('layout.badge.unreadNotifications', { count: unreadCount })}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <NotificationsDropdown
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />
        </div>

        {/* User avatar - click to go to profile */}
        {user && (
          <button 
            onClick={() => navigate('/profile')}
            className="relative w-10 h-10 md:w-8 md:h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center hover:ring-2 hover:ring-primary-500 transition-all min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
          >
            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {user.firstName[0]}{user.lastName[0]}
            </span>
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;